import { apiClient } from './apiClient';

export type EmailTemplateCategory =
  | 'lead_follow_up'
  | 'prospect_follow_up'
  | 'client_check_in'
  | 'upsell'
  | 're_engagement'
  | 'general';

export const EMAIL_TEMPLATE_CATEGORIES: Array<{ value: EmailTemplateCategory; label: string }> = [
  { value: 'lead_follow_up', label: 'Lead follow-up' },
  { value: 'prospect_follow_up', label: 'Prospect follow-up' },
  { value: 'client_check_in', label: 'Client check-in' },
  { value: 'upsell', label: 'Upsell' },
  { value: 're_engagement', label: 'Re-engagement' },
  { value: 'general', label: 'General' },
];

export interface EmailAccountConnection {
  id: string;
  provider: 'google' | 'microsoft';
  email_address: string;
  display_name: string;
  is_active: boolean;
  is_primary: boolean;
  allow_org_sending: boolean;
  connected_by_user_id: string | null;
  connected_by_email: string | null;
  connected_by_name: string | null;
  is_owned_by_current_user: boolean;
  token_expires_at: string | null;
  last_sync_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface EmailTemplate {
  id: string;
  name: string;
  category: EmailTemplateCategory;
  description: string;
  subject_template: string;
  body_template: string;
  default_follow_up_delay_days: number | null;
  is_system_template: boolean;
  is_shared: boolean;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface EmailTemplateWritePayload {
  name: string;
  category: EmailTemplateCategory;
  subject_template: string;
  body_template: string;
  description?: string;
  default_follow_up_delay_days?: number | null;
  is_shared?: boolean;
  is_active?: boolean;
}

export interface OutboundEmailRow {
  id: string;
  client_id: string | null;
  lead_id: string | null;
  connection_id?: string | null;
  subject: string;
  body: string;
  cc?: string;
  bcc?: string;
  related_contact_email: string;
  related_contact_name: string;
  status: string;
  scheduled_send_at?: string | null;
  sent_at: string | null;
  cancelled_at?: string | null;
  failure_reason: string;
  provider: string;
  sender_email: string | null;
  template_name: string | null;
  template_name_snapshot?: string;
  step_number?: number;
  campaign_id?: string | null;
  delivery_channel?: string;
  created_at: string;
}

export interface ScheduledEmailUpdatePayload {
  send_at?: string;
  subject?: string;
  body?: string;
  cc?: string;
  bcc?: string;
  connection_id?: string | null;
  reminder_at?: string | null;
}

export interface CommunicationSummary {
  do_not_contact: boolean;
  unsubscribed_at: string | null;
  last_outbound: OutboundEmailRow | null;
  last_inbound_reply: {
    id: string;
    reply_text: string;
    response_type: string;
    created_at: string;
  } | null;
  recent_outbound: OutboundEmailRow[];
  recent_inbound: Array<{
    id: string;
    reply_text: string;
    response_type: string;
    created_at: string;
  }>;
}

export interface MessagePerformanceRow {
  template_id: string | null;
  template_name: string;
  sent_count: number;
  reply_count: number;
  positive_count: number;
  neutral_count: number;
  negative_count: number;
  opt_out_count: number;
}

export interface EmailReminderRow {
  id: string;
  outbound_email: string;
  outbound_subject: string;
  client: string;
  remind_at: string;
  status: string;
  note: string;
  created_at: string;
  updated_at: string;
}

export type CampaignStatus = 'draft' | 'queued' | 'sending' | 'completed' | 'failed' | 'cancelled';

export interface CampaignRecipientRow {
  id: string;
  client_id: string | null;
  lead_id: string | null;
  email: string;
  personalized_subject: string;
  personalized_body: string;
  status: string;
  failure_reason: string;
  sent_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface CampaignRow {
  id: string;
  name: string;
  subject: string;
  body_template: string;
  status: CampaignStatus;
  total_selected: number;
  total_recipients: number;
  skipped_count: number;
  queued_count: number;
  sent_count: number;
  failed_count: number;
  reply_count: number;
  sent_at: string | null;
  created_at: string;
  updated_at: string;
  recipients?: CampaignRecipientRow[];
}

export interface CampaignPreviewResponse {
  total_selected: number;
  will_send: number;
  skipped: number;
  skipped_reasons: Record<string, number>;
  sample_personalized_email: {
    email: string;
    contact_name: string;
    subject: string;
    body: string;
  } | null;
  max_recipients: number;
}

class EmailService {
  async listAccounts(): Promise<EmailAccountConnection[]> {
    const res = await apiClient.get('/email/accounts/');
    return (res as { results?: EmailAccountConnection[] }).results ?? (res as EmailAccountConnection[]);
  }

  async listOrganizationAccounts(): Promise<EmailAccountConnection[]> {
    const res = await apiClient.get('/email/accounts/organization/');
    return (res as { results?: EmailAccountConnection[] }).results ?? (res as EmailAccountConnection[]);
  }

  async updateAccountSharing(
    accountId: string,
    allowOrgSending: boolean
  ): Promise<EmailAccountConnection> {
    return apiClient.patch(`/email/accounts/${accountId}/sharing/`, {
      allow_org_sending: allowOrgSending,
    });
  }

  async oauthStart(provider: 'google' | 'microsoft'): Promise<{ authorization_url: string }> {
    return apiClient.get(`/email/oauth/${provider}/start/`);
  }

  async setPrimary(accountId: string): Promise<EmailAccountConnection> {
    return apiClient.post(`/email/accounts/${accountId}/set-primary/`);
  }

  async disconnect(accountId: string): Promise<void> {
    await apiClient.post(`/email/accounts/${accountId}/disconnect/`);
  }

  async listTemplates(params?: {
    category?: EmailTemplateCategory | string;
    context?: 'lead' | 'prospect' | 'client';
    visibility?: 'personal' | 'shared' | 'system';
    is_active?: boolean;
  }): Promise<EmailTemplate[]> {
    const res = await apiClient.get('/email/templates/', params);
    return (res as { results?: EmailTemplate[] }).results ?? (res as EmailTemplate[]);
  }

  async createTemplate(data: EmailTemplateWritePayload): Promise<EmailTemplate> {
    return apiClient.post('/email/templates/', data);
  }

  async updateTemplate(id: string, data: Partial<EmailTemplateWritePayload>): Promise<EmailTemplate> {
    return apiClient.patch(`/email/templates/${id}/`, data);
  }

  async deleteTemplate(id: string): Promise<void> {
    await apiClient.delete(`/email/templates/${id}/`);
  }

  async send(
    payload: {
      subject: string;
      body: string;
      template_id?: string | null;
      connection_id?: string | null;
      cc?: string;
      bcc?: string;
      reminder_at?: string | null;
      send_at?: string | null;
      step_number?: number;
      campaign_id?: string | null;
      delivery_channel?: string;
    } & ({ client_id: string; lead_id?: undefined } | { lead_id: string; client_id?: undefined })
  ): Promise<{ outbound_email_id: string; status: string; scheduled_send_at?: string | null; reminder_id: string | null }> {
    return apiClient.post('/email/send/', payload);
  }

  async updateScheduledEmail(id: string, payload: ScheduledEmailUpdatePayload): Promise<OutboundEmailRow> {
    return apiClient.patch(`/email/scheduled/${id}/`, payload);
  }

  async cancelScheduledEmail(id: string): Promise<OutboundEmailRow> {
    return apiClient.post(`/email/scheduled/${id}/cancel/`);
  }

  async retryFailedEmail(id: string, payload?: { connection_id?: string }): Promise<OutboundEmailRow> {
    return apiClient.post(`/email/failed/${id}/retry/`, payload ?? {});
  }

  async getCommunicationSummary(params: { client_id?: string; lead_id?: string }): Promise<CommunicationSummary> {
    return apiClient.get('/email/communication-summary/', params);
  }

  async getMessagePerformance(): Promise<{ results: MessagePerformanceRow[] }> {
    return apiClient.get('/email/message-performance/');
  }

  async listHistory(params?: { client_id?: string; lead_id?: string }): Promise<{ results: OutboundEmailRow[]; count?: number }> {
    return apiClient.get('/email/history/', params);
  }

  async listReminders(params?: { client_id?: string; lead_id?: string; status?: string }): Promise<EmailReminderRow[]> {
    const res = await apiClient.get('/email/reminders/', params);
    return (res as { results?: EmailReminderRow[] }).results ?? (res as EmailReminderRow[]);
  }

  async patchReminder(id: string, data: Partial<Pick<EmailReminderRow, 'status' | 'note'>>): Promise<EmailReminderRow> {
    return apiClient.patch(`/email/reminders/${id}/`, data);
  }

  async listCampaigns(): Promise<CampaignRow[]> {
    const res = await apiClient.get('/campaigns/');
    return (res as { results?: CampaignRow[] }).results ?? (res as CampaignRow[]);
  }

  async getCampaign(id: string): Promise<CampaignRow> {
    return apiClient.get(`/campaigns/${id}/`);
  }

  async createCampaignDraft(payload: {
    name: string;
    subject: string;
    body_template: string;
    lead_ids?: string[];
    client_ids?: string[];
  }): Promise<CampaignRow> {
    return apiClient.post('/campaigns/', payload);
  }

  async previewCampaign(payload: {
    name: string;
    subject: string;
    body_template: string;
    lead_ids?: string[];
    client_ids?: string[];
  }): Promise<CampaignPreviewResponse> {
    return apiClient.post('/campaigns/preview/', payload);
  }

  async sendCampaign(id: string, connectionId?: string): Promise<CampaignRow> {
    return apiClient.post(`/campaigns/${id}/send/`, {
      ...(connectionId ? { connection_id: connectionId } : {}),
    });
  }

  async cancelCampaign(id: string): Promise<CampaignRow> {
    return apiClient.post(`/campaigns/${id}/cancel/`);
  }

  async addCampaignRecipients(
    id: string,
    payload: { lead_ids?: string[]; client_ids?: string[]; connection_id?: string }
  ): Promise<CampaignRow & { add_summary?: { added: number; sent: number; skipped: number } }> {
    return apiClient.post(`/campaigns/${id}/add-recipients/`, payload);
  }
}

export const emailService = new EmailService();
